declare const window: Window & {
  jspdf: { jsPDF: new (opts: Record<string, unknown>) => JsPdfInstance };
};

import {
  PDF_CONTENT_MARGINS,
  PDF_CONTENT_HEADER,
  PDF_TABLE_STYLE,
  PDF_GENERATE_TABLE_DEFAULTS,
  type PdfFontFamily,
  type PdfFontStyle,
  type PdfPageMargins,
} from '@/components/shared/pdf-viewer/pdf-content.config';
import {
  resolveUnitHeaderLines,
  type PdfCongTyHeaderInput,
} from '@/components/shared/pdf-viewer/pdf-document-header';

export type { PdfPageMargins } from '@/components/shared/pdf-viewer/pdf-content.config';
export type { PdfCongTyHeaderInput } from '@/components/shared/pdf-viewer/pdf-document-header';

interface JsPdfInstance {
  addFileToVFS: (name: string, content: string) => void;
  addFont: (
    postScriptName: string,
    fontName: string,
    fontStyle: string,
    encoding?: string
  ) => void;
  setFont: (fontName: string, fontStyle?: string) => void;
  setFontSize: (size: number) => void;
  setDrawColor: (r: number, g?: number, b?: number) => void;
  setFillColor: (r: number, g?: number, b?: number) => void;
  setLineWidth: (width: number) => void;
  setLineDash?: (pattern: number[], phase?: number) => void;
  getTextWidth?: (text: string) => number;
  text: (text: string | string[], x: number, y: number, options?: Record<string, unknown>) => void;
  splitTextToSize: (text: string, maxWidth: number) => string[];
  rect: (x: number, y: number, w: number, h: number, style?: string) => void;
  line: (x1: number, y1: number, x2: number, y2: number, style?: string) => void;
  addPage: () => void;
  output: (type: string) => string;
  internal: { pageSize: { getWidth: () => number; getHeight: () => number } };
}

export interface TablePdfColumn {
  header: string;
  width: number;
  align?: 'left' | 'right' | 'center';
  type?: 'text' | 'checkbox';
}

export type TablePdfCell = string | boolean;

export interface GenerateTablePdfOptions {
  title: string;
  subtitle?: string;
  columns: TablePdfColumn[];
  rows: TablePdfCell[][];
  fontFamily?: PdfFontFamily;
  /** @deprecated Use `margins` instead */
  margin?: number;
  margins?: Partial<PdfPageMargins>;
  tableFullWidth?: boolean;
  /** Optional top-left "Công ty: …" lines drawn before the centered title */
  documentHeader?: PdfCongTyHeaderInput;
}

const FONT_FILE = 'NotoSans-Regular.ttf';
const FONT_NAME = 'NotoSans';
const TIMES_FONT_FILE = 'Tinos-Regular.ttf';
const TIMES_FONT_BOLD_FILE = 'Tinos-Bold.ttf';
const TIMES_FONT_NAME = 'TimesNewRoman';

/** Matches shared `pdf-print-table` borders (mm). */
const TABLE_OUTER_BORDER_WIDTH = 0.5;
const TABLE_INNER_VERTICAL_WIDTH = 0.15;
const TABLE_SECTION_HORIZONTAL_WIDTH = 0.25;
const TABLE_DOTTED_DASH = [0.6, 0.45];

const CHECKBOX_SIZE = 3.2;

let cachedFontBase64: string | null = null;
let cachedTimesFontBase64: string | null = null;
let cachedTimesBoldFontBase64: string | null = null;

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(
      null,
      bytes.subarray(i, i + chunkSize) as unknown as number[]
    );
  }
  return btoa(binary);
}

async function loadFontBase64(): Promise<string> {
  if (cachedFontBase64) return cachedFontBase64;

  const response = await fetch(`${process.env.BASE_URL}fonts/${FONT_FILE}`);
  if (!response.ok) {
    throw new Error(`Failed to load font: ${FONT_FILE}`);
  }

  cachedFontBase64 = arrayBufferToBase64(await response.arrayBuffer());
  return cachedFontBase64;
}

async function loadTimesFontBase64(): Promise<string> {
  if (cachedTimesFontBase64) return cachedTimesFontBase64;

  const response = await fetch(`${process.env.BASE_URL}fonts/${TIMES_FONT_FILE}`);
  if (!response.ok) {
    throw new Error(`Failed to load font: ${TIMES_FONT_FILE}`);
  }

  cachedTimesFontBase64 = arrayBufferToBase64(await response.arrayBuffer());
  return cachedTimesFontBase64;
}

async function loadTimesBoldFontBase64(): Promise<string> {
  if (cachedTimesBoldFontBase64) return cachedTimesBoldFontBase64;

  const response = await fetch(`${process.env.BASE_URL}fonts/${TIMES_FONT_BOLD_FILE}`);
  if (!response.ok) {
    throw new Error(`Failed to load font: ${TIMES_FONT_BOLD_FILE}`);
  }

  cachedTimesBoldFontBase64 = arrayBufferToBase64(await response.arrayBuffer());
  return cachedTimesBoldFontBase64;
}

async function applyUnicodeFont(pdf: JsPdfInstance) {
  const base64 = await loadFontBase64();
  pdf.addFileToVFS(FONT_FILE, base64);
  pdf.addFont(FONT_FILE, FONT_NAME, 'normal', 'Identity-H');
  pdf.setFont(FONT_NAME, 'normal');
}

async function applyTimesFont(pdf: JsPdfInstance): Promise<string> {
  try {
    const base64 = await loadTimesFontBase64();
    const boldBase64 = await loadTimesBoldFontBase64();
    pdf.addFileToVFS(TIMES_FONT_FILE, base64);
    pdf.addFileToVFS(TIMES_FONT_BOLD_FILE, boldBase64);
    pdf.addFont(TIMES_FONT_FILE, TIMES_FONT_NAME, 'normal', 'Identity-H');
    pdf.addFont(TIMES_FONT_BOLD_FILE, TIMES_FONT_NAME, 'bold', 'Identity-H');
    pdf.setFont(TIMES_FONT_NAME, 'normal');
    return TIMES_FONT_NAME;
  } catch {
    pdf.setFont('times', 'normal');
    return 'times';
  }
}

async function applyFont(pdf: JsPdfInstance, fontFamily: PdfFontFamily): Promise<string> {
  if (fontFamily === 'times') {
    return applyTimesFont(pdf);
  }

  await applyUnicodeFont(pdf);
  return FONT_NAME;
}

function setSolidLine(pdf: JsPdfInstance, width: number) {
  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(width);
  pdf.setLineDash?.([], 0);
}

function setDottedLine(pdf: JsPdfInstance, width: number) {
  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(width);
  pdf.setLineDash?.(TABLE_DOTTED_DASH, 0);
}

function measureLabelWidth(pdf: JsPdfInstance, label: string): number {
  if (typeof pdf.getTextWidth === 'function') {
    return pdf.getTextWidth(label);
  }
  return label.length * 1.65;
}

function getTextX(x: number, col: TablePdfColumn): number {
  const pad = PDF_TABLE_STYLE.cellPadding;
  if (col.align === 'right') return x + col.width - pad;
  if (col.align === 'center') return x + col.width / 2;
  return x + pad;
}

function drawCellText(
  pdf: JsPdfInstance,
  lines: string[],
  x: number,
  y: number,
  col: TablePdfColumn,
  _cellHeight: number,
  fontSize: number,
  fontName: string,
  fontStyle: PdfFontStyle = PDF_TABLE_STYLE.bodyFontStyle
) {
  pdf.setFont(fontName, fontStyle);
  pdf.setFontSize(fontSize);
  const pad = PDF_TABLE_STYLE.cellPadding;
  const textLineHeight = PDF_TABLE_STYLE.lineHeight;
  // Top-align: baseline of first line sits just below the top padding.
  const textY = y + pad + textLineHeight * 0.75;

  pdf.text(lines, getTextX(x, col), textY, {
    align: col.align ?? 'left',
    maxWidth: col.width - pad * 2,
  });
}

/** Draws a print-style checkbox (black square + optional checkmark). */
function drawCheckboxCell(
  pdf: JsPdfInstance,
  x: number,
  y: number,
  cellWidth: number,
  _cellHeight: number,
  checked: boolean
) {
  const size = CHECKBOX_SIZE;
  const pad = PDF_TABLE_STYLE.cellPadding;
  const cx = x + (cellWidth - size) / 2;
  const cy = y + pad;

  setSolidLine(pdf, 0.2);
  if (checked) {
    pdf.setFillColor(0, 0, 0);
    pdf.rect(cx, cy, size, size, 'FD');

    pdf.setDrawColor(255, 255, 255);
    pdf.setLineWidth(0.35);
    pdf.line(cx + 0.7, cy + size * 0.55, cx + size * 0.38, cy + size * 0.82);
    pdf.line(cx + size * 0.38, cy + size * 0.82, cx + size * 0.88, cy + size * 0.22);
  } else {
    pdf.setFillColor(255, 255, 255);
    pdf.rect(cx, cy, size, size, 'FD');
  }
}

function isCheckboxColumn(col: TablePdfColumn): boolean {
  return col.type === 'checkbox';
}

function cellToText(cell: TablePdfCell): string {
  return typeof cell === 'boolean' ? '' : cell ?? '';
}

function resolveMargins(options: GenerateTablePdfOptions): PdfPageMargins {
  if (typeof options.margin === 'number') {
    return {
      top: options.margin,
      bottom: options.margin,
      left: options.margin,
      right: options.margin,
    };
  }

  return { ...PDF_CONTENT_MARGINS, ...options.margins };
}

function scaleColumnsToWidth(columns: TablePdfColumn[], targetWidth: number): TablePdfColumn[] {
  const baseWidth = columns.reduce((sum, col) => sum + col.width, 0);
  if (!baseWidth || baseWidth === targetWidth) return columns;

  const scale = targetWidth / baseWidth;
  return columns.map((col) => ({
    ...col,
    width: col.width * scale,
  }));
}

/**
 * Draw shared pdf-print-table borders for one page table segment:
 * thick outer box, solid verticals, solid header bottom, dotted data row bottoms.
 */
function drawPrintTableBorders(
  pdf: JsPdfInstance,
  startX: number,
  tableTop: number,
  tableBottom: number,
  columns: TablePdfColumn[],
  headerBottom: number,
  dataRowBottoms: number[]
) {
  const tableWidth = columns.reduce((sum, col) => sum + col.width, 0);

  setSolidLine(pdf, TABLE_INNER_VERTICAL_WIDTH);
  let x = startX;
  for (let i = 0; i < columns.length - 1; i++) {
    x += columns[i].width;
    pdf.line(x, tableTop, x, tableBottom);
  }

  if (headerBottom > tableTop && headerBottom <= tableBottom) {
    setSolidLine(pdf, TABLE_SECTION_HORIZONTAL_WIDTH);
    pdf.line(startX, headerBottom, startX + tableWidth, headerBottom);
  }

  setDottedLine(pdf, TABLE_SECTION_HORIZONTAL_WIDTH);
  dataRowBottoms.forEach((rowBottom, index) => {
    if (index === dataRowBottoms.length - 1) return;
    pdf.line(startX, rowBottom, startX + tableWidth, rowBottom);
  });
  pdf.setLineDash?.([], 0);

  setSolidLine(pdf, TABLE_OUTER_BORDER_WIDTH);
  pdf.rect(startX, tableTop, tableWidth, tableBottom - tableTop, 'S');
}

export async function generateTablePdf(options: GenerateTablePdfOptions): Promise<string> {
  const fontFamily = options.fontFamily ?? PDF_GENERATE_TABLE_DEFAULTS.fontFamily;
  const margins = resolveMargins(options);
  const tableFullWidth = options.tableFullWidth ?? PDF_GENERATE_TABLE_DEFAULTS.tableFullWidth;

  const pdf = new window.jspdf.jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4',
  });

  const fontName = await applyFont(pdf, fontFamily);

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const {
    lineHeight,
    cellPadding,
    headerHeight,
    headerFontSize,
    headerFontStyle,
    bodyFontSize,
    bodyFontStyle,
  } = PDF_TABLE_STYLE;
  let y = margins.top;

  if (options.documentHeader) {
    const unitLines = resolveUnitHeaderLines({
      ...options.documentHeader,
      variant: options.documentHeader.variant ?? 'masterList',
    });
    pdf.setFontSize(9);
    unitLines.forEach((line) => {
      if (line.valueOnly) {
        pdf.setFont(fontName, 'bold');
        pdf.text(line.value, margins.left, y, { align: 'left' });
      } else if (line.bold) {
        pdf.setFont(fontName, 'bold');
        pdf.text(`${line.label}: ${line.value}`, margins.left, y, {
          align: 'left',
        });
      } else {
        const label = `${line.label}: `;
        pdf.setFont(fontName, 'bold');
        pdf.text(label, margins.left, y, { align: 'left' });
        const labelWidth = measureLabelWidth(pdf, label);
        pdf.setFont(fontName, bodyFontStyle);
        pdf.text(line.value, margins.left + labelWidth, y, { align: 'left' });
      }
      y += 5;
    });
    y += 2;
  }

  pdf.setFont(fontName, PDF_CONTENT_HEADER.titleFontStyle);
  pdf.setFontSize(PDF_CONTENT_HEADER.titleFontSize);
  pdf.text(options.title, pageWidth / 2, y, { align: 'center' });
  y += PDF_CONTENT_HEADER.titleSpacing;

  if (options.subtitle) {
    pdf.setFont(fontName, PDF_CONTENT_HEADER.subtitleFontStyle);
    pdf.setFontSize(PDF_CONTENT_HEADER.subtitleFontSize);
    pdf.text(options.subtitle, pageWidth / 2, y, { align: 'center' });
    y += PDF_CONTENT_HEADER.subtitleSpacing;
  }

  pdf.setFont(fontName, bodyFontStyle);

  const availableTableWidth = pageWidth - margins.left - margins.right;
  const columns = tableFullWidth
    ? scaleColumnsToWidth(options.columns, availableTableWidth)
    : options.columns;
  const tableWidth = columns.reduce((sum, col) => sum + col.width, 0);
  const startX = margins.left + Math.max(0, (availableTableWidth - tableWidth) / 2);

  const measureRow = (cells: TablePdfCell[], fontSize: number): number => {
    pdf.setFontSize(fontSize);
    let maxLines = 1;
    cells.forEach((cell, i) => {
      if (isCheckboxColumn(columns[i])) return;
      const lines = pdf.splitTextToSize(
        cellToText(cell),
        columns[i].width - cellPadding * 2
      );
      maxLines = Math.max(maxLines, lines.length);
    });
    // Compact body rows: height follows content + padding (no large fixed floor).
    return maxLines * lineHeight + cellPadding * 2;
  };

  let segmentTop = y;
  let headerBottom = y + headerHeight;
  const dataRowBottoms: number[] = [];

  const finishSegmentBorders = () => {
    if (y <= segmentTop) return;
    drawPrintTableBorders(
      pdf,
      startX,
      segmentTop,
      y,
      columns,
      headerBottom,
      dataRowBottoms
    );
  };

  const startNewSegment = () => {
    segmentTop = y;
    headerBottom = y + headerHeight;
    dataRowBottoms.length = 0;
  };

  const drawHeader = () => {
    let x = startX;

    columns.forEach((col) => {
      pdf.setFillColor(255, 255, 255);
      pdf.rect(x, y, col.width, headerHeight, 'F');
      pdf.setFont(fontName, headerFontStyle);
      pdf.setFontSize(headerFontSize);
      pdf.text(col.header, x + col.width / 2, y + 5.5, {
        align: 'center',
        maxWidth: col.width - cellPadding * 2,
      });
      x += col.width;
    });

    y += headerHeight;
    headerBottom = y;
  };

  const drawRow = (cells: TablePdfCell[]) => {
    const rowHeight = measureRow(cells, bodyFontSize);

    if (y + rowHeight > pageHeight - margins.bottom) {
      finishSegmentBorders();
      pdf.addPage();
      y = margins.top;
      startNewSegment();
      drawHeader();
    }

    let x = startX;
    const cellLines = cells.map((cell, i) => {
      if (isCheckboxColumn(columns[i])) return [];
      return pdf.splitTextToSize(
        cellToText(cell),
        columns[i].width - cellPadding * 2
      );
    });

    columns.forEach((col, i) => {
      pdf.setFillColor(255, 255, 255);
      pdf.rect(x, y, col.width, rowHeight, 'F');

      if (isCheckboxColumn(col)) {
        drawCheckboxCell(pdf, x, y, col.width, rowHeight, cells[i] === true);
      } else {
        drawCellText(
          pdf,
          cellLines[i],
          x,
          y,
          col,
          rowHeight,
          bodyFontSize,
          fontName,
          bodyFontStyle
        );
      }

      x += col.width;
    });

    y += rowHeight;
    dataRowBottoms.push(y);
  };

  drawHeader();
  options.rows.forEach((row) => drawRow(row));
  finishSegmentBorders();

  return pdf.output('datauristring');
}
